import { useState, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import * as _m from "~/paraglide/messages";
import { asAnalyticsTier } from "~/lib/analytics-events";
import { trackEvent } from "~/lib/posthog-client";

const m = _m as unknown as Record<string, ((...args: unknown[]) => string) | undefined>;

function msg(key: string, fallback: string, params?: Record<string, string>): string {
  try {
    const fn = m[key];
    return typeof fn === "function" ? fn(params ?? {}) : fallback;
  } catch {
    return fallback;
  }
}

interface LowCreditBannerProps {
  creditsRemaining: number;
  tier: string;
  resetAt?: string;
}

const LOW_CREDIT_THRESHOLDS: Record<string, number> = {
  free: 2,
  student: 10,
  pro: 20,
};

const DISMISS_KEY = "lowCreditBannerDismissed";

export function LowCreditBanner({
  creditsRemaining,
  tier,
}: LowCreditBannerProps) {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem(DISMISS_KEY) === "true";
  });
  const shownRef = useRef(false);

  const threshold = LOW_CREDIT_THRESHOLDS[tier] ?? 2;
  const shouldShow = creditsRemaining <= threshold && !dismissed;

  // Track banner shown event once
  useEffect(() => {
    if (shouldShow && !shownRef.current) {
      shownRef.current = true;
      trackEvent("feature_used", { feature: "low_credit_banner", state: "shown" });
    }
  }, [shouldShow, tier, creditsRemaining]);

  if (!shouldShow) return null;

  function handleDismiss() {
    sessionStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  }

  function handleUpgradeClick() {
    trackEvent("upgrade_clicked", { source: "low_credit_banner", current_tier: asAnalyticsTier(tier) });
  }

  return (
    <div
      className="flex items-center justify-between gap-3 rounded-[10px] px-4 py-2.5 text-sm"
      style={{
        backgroundColor: "var(--warning-subtle)",
        color: "var(--warning)",
        border: "1px solid oklch(0.7 0.12 85 / 0.2)",
      }}
    >
      <p className="flex-1 font-medium">
        {msg("low_credit_banner", `You have ${creditsRemaining} AI actions remaining this month.`, { count: String(creditsRemaining) })}
        {" — top up anytime with a credit pack."}
      </p>

      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/pricing"
          hash="packs"
          onClick={() => {
            trackEvent("cta_clicked", { cta: "buy_pack", location: "low_credit_banner" });
          }}
          className="text-xs font-medium transition-colors duration-200 hover:underline"
          style={{ color: "var(--accent)" }}
        >
          Buy credits
        </Link>
        {tier !== "pro" && (
          <Link
            to="/pricing"
            onClick={handleUpgradeClick}
            className="inline-flex items-center rounded-[8px] px-3 py-1 text-xs font-semibold text-white transition-all duration-300 hover:opacity-90"
            style={{ backgroundColor: "var(--accent)" }}
          >
            {msg("low_credit_banner_upgrade", "Upgrade")}
          </Link>
        )}
        <button
          onClick={handleDismiss}
          className="inline-flex items-center rounded-[8px] px-3 py-1 text-xs font-medium transition-colors duration-200 hover:opacity-70"
          style={{ color: "var(--warning)" }}
        >
          {msg("low_credit_banner_dismiss", "Dismiss")}
        </button>
      </div>
    </div>
  );
}
